import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import {
	Button,
	FormControl,
	FormControlText,
	FormGroup,
	Input,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Modalv2
} from "@itandi/itandi-bb-ui";
import type React from "react";
import { useId } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import type {
	LineUserAccount,
	LineUserAccountEdit
} from "~/models/LineUserAccount";
import { buildInitialLineUserAccount } from "~/models/LineUserAccount";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/vendors/LineUserAccounts";
import { generatePath } from "~/utils/generatePath";

type Props = DeepReadonly<{
	handleClose: () => void;
	lineUserAccount?: LineUserAccount | null;
}>;

export const LineUserAccountModal: React.FC<Props> = ({
	handleClose,
	lineUserAccount = null
}: Props) => {
	const formId = useId();
	const { mutate: globalMutate } = useSWRConfig();
	const methods = useForm<LineUserAccountEdit>({
		defaultValues: lineUserAccount ?? buildInitialLineUserAccount(),
		mode: "onBlur"
	});
	const { errors, isDirty, isSubmitting } = methods.formState;
	const newRecord = lineUserAccount === null;

	const onSubmit = async (values: LineUserAccountEdit): Promise<void> => {
		const result = newRecord
			? await create(values)
			: await update(values, lineUserAccount.id);
		await globalMutate(generatePath("/vendors/line_user_accounts"));
		if (isSuccess(result)) {
			toast.success(newRecord ? "追加しました。" : "更新しました。");
			handleClose();
		} else {
			toast.error(`エラーが発生しました。${result.message}`);
		}
	};

	return (
		<Modalv2 isOpen onClose={handleClose} size="s">
			<ModalHeader title="LINEユーザーアカウント設定" />
			<ModalBody>
				<form id={formId} noValidate onSubmit={methods.handleSubmit(onSubmit)}>
					<FormGroup variant="vertical">
						{newRecord ? (
							<FormControl isInvalid={!!errors.token} label="認証コード">
								<Input
									{...methods.register("token", {
										required: "入力してください"
									})}
								/>
								<FormControlText error={errors.token?.message} />
							</FormControl>
						) : null}
						<FormControl isInvalid={!!errors.name} label="送信先の名前">
							<Input
								{...methods.register("name", {
									required: "入力してください"
								})}
							/>
							<FormControlText error={errors.name?.message} />
						</FormControl>
					</FormGroup>
				</form>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						disabled={isSubmitting}
						onClick={handleClose}
						type="button"
						variant="secondary"
					>
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						disabled={!isDirty}
						form={formId}
						loading={isSubmitting}
						type="submit"
						variant="primary"
					>
						{newRecord ? "追加" : "更新"}
					</Button>
				}
			/>
		</Modalv2>
	);
};
